/**
 * Main router configuration for Lytstore
 * Combines shopper and admin routes with layout-based routing
 */

import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { useUserStore } from '@/stores/useUserStore'
import { shopperRoutes } from './shopper.routes'
import { adminRoutes } from './admin.routes'
import { staffRoutes } from './staff.routes'
import { devRoutes } from './dev.routes'
import { requiresAuthentication } from '@/types/router.types'
import { trackUsagePage } from '@/utils/telemetry'

/**
 * Check if route requires staff authentication (federated from Lytning)
 */
function requiresStaffAuth(route: RouteLocationNormalized): boolean {
  return route.matched.some(record => record.meta?.requiresStaffAuth === true)
}

// Combine all routes
// Note: Admin routes use staffAuthGuard via beforeEnter for federated auth from Lytning
// The global auth check below only applies to shopper routes requiring regular user auth
const routes = [
  ...shopperRoutes,
  ...adminRoutes,
  ...staffRoutes,
  ...devRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundPage.vue'),
    meta: { title: 'Page Not Found' }
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition

    // Keep position on same-path navigations (query/filter changes)
    if (to.path === from.path && !to.hash) return false

    if (to.hash) return { el: to.hash, behavior: 'smooth' }

    // Otherwise, scroll to top
    return { top: 0, behavior: 'smooth' }
  },
})

// Global before guard to check authentication
router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next) => {
  console.info('[Router] navigating', {
    from: from.fullPath,
    to: to.fullPath,
    name: to.name,
    params: to.params,
    query: to.query
  })

  // Skip global auth check for staff-authenticated routes
  // Staff auth is handled by staffAuthGuard via beforeEnter on admin routes
  if (requiresStaffAuth(to)) {
    // staffAuthGuard handles this via beforeEnter, just proceed
    next()
    return
  }

  // Use Pinia store for authentication check (not legacy store)
  const userStore = useUserStore()
  const isAuthenticated = userStore.isLoggedIn

  // Check if the route requires regular user authentication (shopper routes)
  if (requiresAuthentication(to) && !isAuthenticated) {
    // Redirect to login page with redirect parameter
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
    return
  }

  next()
})

// Global after hook to update document title and record usage page views
router.afterEach((to: RouteLocationNormalized) => {
  // Update document title based on route meta
  const title = to.meta?.title as string | undefined
  if (title) {
    document.title = `${title} | shop.co.zm`
  } else {
    document.title = 'shop.co.zm'
  }

  // Record page view for usage analytics
  trackUsagePage('page:' + String(to.name || to.path))
})

// Log navigation errors
router.onError((error, to, from) => {
  console.error('[Router] navigation error', {
    error,
    from: from?.fullPath,
    to: to?.fullPath
  })
})

export default router
